/**
 * A new task or a thing Denker learned, as a wall card among the frames
 * (Part 09 decisions 5 and 6; mockup `ncard`, `NC.task`, `NC.memory`).
 *
 * - Task: the task paint, "New task" with its key, the title, the status
 *   and the assignee (`TaskCardFace`, which its pocket's header shares).
 *   The pill: Status (its transitions) · More (Show on board, More
 *   details, Delete task). A click docks its pocket.
 * - Thing: the memory paint, the node glyph, "Denker learned" with its
 *   kind, the name and what it learned: the newest fact's claim, else the
 *   description (`ThingCardFace`, shared likewise). The pill: Show in
 *   memory · More (Hide: off the wall on every device, the memory stays;
 *   Forget, after its confirmation). A click shows it in memory, the memory
 *   frame in Focus on the thing.
 *
 * Opening either marks it opened (`lib/wall-new-opened.ts`): the card goes.
 */
import { memo, useState, type ReactNode } from "react";
import { hideThingFromWall } from "@/api/memory";
import type { TaskStatus } from "@/api/tasks";
import type { ThingKind } from "@/api/memory-focus";
import {
  Button,
  CONCEPT_ICONS,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icons,
} from "@/components/ui";
import {
  openTaskInProject,
  type ProjectItemTarget,
} from "@/components/layout/cross-project-navigation";
import {
  changeProjectTaskStatus,
  deleteProjectTask,
  showTaskMoreDetails,
} from "@/components/panels/task-actions";
import { taskStatusOptions } from "@/components/panels/task-detail-config";
import { ForgetThingDialog } from "@/components/shapes/memory-frame/forget-thing-dialog";
import { openProjectMemory } from "@/components/shapes/memory-frame/open-project-memory";
import {
  TaskCardFace,
  ThingCardFace,
} from "@/components/shapes/shared/paint-card-faces";
import {
  STATUS_DOT_COLORS,
  TASK_STATUS_LABEL_KEYS,
} from "@/components/shapes/taskboard-frame/task-metadata-chips";
import { useT } from "@/i18n";
import { framesMemoryMessages } from "@/i18n/messages/frames-memory";
import { framesTaskboardMessages } from "@/i18n/messages/frames-taskboard";
import { panelsTasksMessages } from "@/i18n/messages/panels-tasks";
import { wallMessages } from "@/i18n/messages/wall";
import { cn } from "@/lib/cn";
import { showTaskOnBoard } from "@/lib/show-task-on-board";
import { useProjectStore } from "@/stores/project-store";
import {
  dropWallNewTask,
  dropWallNewThing,
  patchWallNewTask,
  type WallNewItem,
  type WallNewTask,
  type WallNewThing,
} from "./use-wall-new-items";
import { WALL_CARD_SURFACE_CLASS } from "./wall-card";
import {
  WALL_HOVER_PILL_CLASS,
  WALL_PILL_BUTTON_CLASS,
} from "./wall-card-actions";

/** The pill stays while one of its menus is open. */
const PILL_OPEN_CLASS =
  "has-[[data-state=open]]:pointer-events-auto has-[[data-state=open]]:translate-y-0 has-[[data-state=open]]:opacity-100";
const ICON_CLASS = "h-3.5 w-3.5";

function taskTarget(task: WallNewTask): ProjectItemTarget {
  const project = useProjectStore
    .getState()
    .projects.find((candidate) => candidate.id === task.projectId);
  return {
    id: task.id,
    projectId: task.projectId,
    projectName: project?.name ?? null,
  };
}

async function changeStatus(task: WallNewTask, status: TaskStatus) {
  if (await changeProjectTaskStatus(task.projectId, task.id, status)) {
    patchWallNewTask(task.id, status);
  }
}

async function deleteNewTask(task: WallNewTask) {
  if (await deleteProjectTask(task.projectId, task.id)) {
    dropWallNewTask(task.id);
  }
}

function TaskPill({ task }: { task: WallNewTask }) {
  const t = useT(framesTaskboardMessages);
  const tTasks = useT(panelsTasksMessages);
  const tWall = useT(wallMessages);
  const status = `${tTasks("status")}: ${t(TASK_STATUS_LABEL_KEYS[task.status])}`;
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="iconSm"
            className={WALL_PILL_BUTTON_CLASS}
            aria-label={status}
            title={status}
            data-testid={`wall-new-task-status-${task.id}`}
          >
            <i
              className="h-2 w-2 rounded-full"
              style={{ background: STATUS_DOT_COLORS[task.status] }}
              aria-hidden="true"
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {taskStatusOptions(task.status).map((option) => (
            <DropdownMenuItem
              key={option.value}
              destructive={option.destructive}
              onSelect={() => void changeStatus(task, option.value)}
              data-testid={`wall-new-task-status-${task.id}-option-${option.value}`}
            >
              {option.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="iconSm"
            className={WALL_PILL_BUTTON_CLASS}
            aria-label={tTasks("more")}
            title={tTasks("more")}
            data-testid={`wall-new-task-more-${task.id}`}
          >
            <Icons.MoreHorizontal className={ICON_CLASS} aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            icon={<Icons.Kanban />}
            onSelect={() => void showTaskOnBoard(taskTarget(task))}
            data-testid={`wall-new-task-show-board-${task.id}`}
          >
            {tWall("showOnBoard")}
          </DropdownMenuItem>
          <DropdownMenuItem
            icon={<Icons.Info />}
            onSelect={() => void showTaskMoreDetails(taskTarget(task))}
            data-testid={`wall-new-task-details-${task.id}`}
          >
            {tTasks("moreDetails")}
          </DropdownMenuItem>
          <DropdownMenuSeparator className="h-2 bg-transparent" />
          <DropdownMenuItem
            destructive
            icon={<Icons.Trash2 />}
            onSelect={() => void deleteNewTask(task)}
            data-testid={`wall-new-task-delete-${task.id}`}
          >
            {tTasks("deleteTask")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}

const MemoryIcon = CONCEPT_ICONS.memory;

async function hideThing(thing: WallNewThing) {
  try {
    await hideThingFromWall(thing.id);
  } catch {
    return; // The request's toast says why; the card stays.
  }
  dropWallNewThing(thing.id);
}

function ThingPill({ thing }: { thing: WallNewThing }) {
  const t = useT(wallMessages);
  const tTasks = useT(panelsTasksMessages);
  const tMemory = useT(framesMemoryMessages);
  const [confirmForget, setConfirmForget] = useState(false);
  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="iconSm"
        className={WALL_PILL_BUTTON_CLASS}
        aria-label={t("showInMemory")}
        title={t("showInMemory")}
        onClick={() => openProjectMemory(thing.id)}
        data-testid={`wall-new-thing-show-${thing.id}`}
      >
        <MemoryIcon className={ICON_CLASS} aria-hidden="true" />
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="iconSm"
            className={WALL_PILL_BUTTON_CLASS}
            aria-label={tTasks("more")}
            title={tTasks("more")}
            data-testid={`wall-new-thing-more-${thing.id}`}
          >
            <Icons.MoreHorizontal className={ICON_CLASS} aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            icon={<Icons.EyeOff />}
            onSelect={() => void hideThing(thing)}
            data-testid={`wall-new-thing-hide-${thing.id}`}
          >
            {t("hideFromWall")}
          </DropdownMenuItem>
          <DropdownMenuSeparator className="h-2 bg-transparent" />
          <DropdownMenuItem
            destructive
            icon={<Icons.Trash2 />}
            onSelect={() => setConfirmForget(true)}
            data-testid={`wall-new-thing-forget-${thing.id}`}
          >
            {tMemory("forget")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {confirmForget ? (
        <ForgetThingDialog
          thingId={thing.id}
          name={thing.name}
          onClose={() => setConfirmForget(false)}
          testId={`wall-new-thing-forget-confirm-${thing.id}`}
        />
      ) : null}
    </>
  );
}

function WallNewCardBody({ item }: { item: WallNewItem }) {
  const t = useT(wallMessages);
  let label: string;
  let open: () => void;
  let face: ReactNode;
  let pill: ReactNode;
  if (item.kind === "task") {
    const { task } = item;
    label = t("newTaskLabel", { title: task.title });
    open = () => void openTaskInProject(taskTarget(task));
    face = (
      <TaskCardFace
        eyebrow={t("newTask")}
        ticket={task.ticketDisplay}
        title={<p className="line-clamp-3">{task.title}</p>}
        status={task.status}
        assigneeName={task.assigneeName}
        testIds={{
          ticket: `wall-new-task-key-${task.id}`,
          status: `wall-new-task-status-label-${task.id}`,
        }}
      />
    );
    pill = <TaskPill task={task} />;
  } else {
    const { thing } = item;
    label = t("denkerLearnedLabel", { name: thing.name });
    open = () => openProjectMemory(thing.id);
    face = (
      <ThingCardFace
        name={thing.name}
        kind={thing.nodeType as ThingKind}
        description={thing.learned}
      />
    );
    pill = <ThingPill thing={thing} />;
  }
  return (
    <div
      className="group/wall-item relative"
      // The card takes the pointer on the desktop, as a frame card does.
      data-interactive="true"
      data-wall-card=""
    >
      <div className={WALL_CARD_SURFACE_CLASS}>
        <button
          type="button"
          className="block w-full cursor-pointer select-none text-left outline-none [font-size:var(--wall-fs,13px)]"
          aria-label={label}
          title={label}
          onClick={open}
          data-testid={`wall-new-card-${item.key}`}
        >
          {face}
        </button>
        <div
          className={cn(WALL_HOVER_PILL_CLASS, PILL_OPEN_CLASS)}
          data-denker-glass-force="css"
          data-interactive="true"
          data-testid={`wall-new-card-actions-${item.key}`}
        >
          {pill}
        </div>
      </div>
    </div>
  );
}

/** A new task or thing card: re-renders only for its own item. */
export const WallNewCard = memo(WallNewCardBody);
